import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { membershipsQuery } from '@/access/memberships'
import { api } from '@/api/client'
import { queryClient } from '@/api/queryClient'
import { sessionStore } from '@/auth/sessionStore'
import { ToastProvider, useToast } from '@/ui'
import { telemetry } from './telemetry'

// Dev-only persona switcher: the import (and MSW with it) is dropped from non-mock builds.
// (Read straight from import.meta.env so Vite can fold the condition at build time.)
const PersonaSwitcher =
  import.meta.env.VITE_API_MODE === 'mock' ? lazy(() => import('@/mocks/PersonaSwitcher')) : null

/** Cross-cutting reactions to API/session events. */
function Watchers() {
  const toast = useToast()

  // 403 with required_capability: our cached capabilities are stale. Tell the user and re-gate.
  useEffect(
    () =>
      api.onForbidden((e) => {
        telemetry.breadcrumb('access', '403 required_capability', {
          required_capability: e.requiredCapability,
        })
        toast('Seu acesso mudou. Atualizamos suas permissões.', { icon: 'lock' })
        void queryClient.invalidateQueries({ queryKey: membershipsQuery.queryKey })
      }),
    [toast],
  )

  // Session ended (logout here, forced logout after a second 401, or another tab): drop cached data.
  useEffect(
    () =>
      sessionStore.subscribe(() => {
        if (!sessionStore.hasSession()) queryClient.clear()
      }),
    [],
  )

  return null
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Watchers />
        {children}
        {PersonaSwitcher && (
          <Suspense fallback={null}>
            <PersonaSwitcher />
          </Suspense>
        )}
      </ToastProvider>
    </QueryClientProvider>
  )
}
